import { useCallback, useMemo, useRef, useState } from "react";

const AUDIO_PREFS_KEY = "clubbage-audio-prefs";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const createPinkNoiseBuffer = (audioContext) => {
    const durationSeconds = 0.25;
    const sampleRate = audioContext.sampleRate;
    const buffer = audioContext.createBuffer(1, Math.floor(sampleRate * durationSeconds), sampleRate);
    const data = buffer.getChannelData(0);

    let b0 = 0;
    let b1 = 0;
    let b2 = 0;

    for (let i = 0; i < data.length; i += 1) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99765 * b0 + white * 0.0990460;
        b1 = 0.96300 * b1 + white * 0.2965164;
        b2 = 0.57000 * b2 + white * 1.0526913;
        data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.08;
    }

    return buffer;
};

const playWhoosh = (audioContext, noiseBuffer, when, gainAmount, durationMs, frequency) => {
    const source = audioContext.createBufferSource();
    source.buffer = noiseBuffer;

    const filter = audioContext.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(frequency, when);
    filter.Q.setValueAtTime(0.8, when);

    const gainNode = audioContext.createGain();
    const durationSeconds = durationMs / 1000;
    const releaseTime = when + durationSeconds;

    gainNode.gain.setValueAtTime(0.0001, when);
    gainNode.gain.exponentialRampToValueAtTime(Math.max(0.01, gainAmount), when + 0.012);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, releaseTime);

    source.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(audioContext.destination);

    source.start(when);
    source.stop(releaseTime + 0.03);
};

const playImpact = (audioContext, noiseBuffer, when, gainAmount, bodyHit = false) => {
    const impactTone = audioContext.createOscillator();
    const impactToneGain = audioContext.createGain();

    impactTone.type = bodyHit ? "triangle" : "square";
    impactTone.frequency.setValueAtTime(bodyHit ? 115 : 155, when);
    impactTone.frequency.exponentialRampToValueAtTime(bodyHit ? 70 : 95, when + 0.09);

    impactToneGain.gain.setValueAtTime(Math.max(0.01, gainAmount), when);
    impactToneGain.gain.exponentialRampToValueAtTime(0.0001, when + 0.1);

    const noiseSource = audioContext.createBufferSource();
    noiseSource.buffer = noiseBuffer;

    const noiseFilter = audioContext.createBiquadFilter();
    noiseFilter.type = "lowpass";
    noiseFilter.frequency.setValueAtTime(bodyHit ? 820 : 1300, when);

    const noiseGain = audioContext.createGain();
    noiseGain.gain.setValueAtTime(Math.max(0.01, gainAmount * 0.7), when);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, when + 0.11);

    impactTone.connect(impactToneGain);
    impactToneGain.connect(audioContext.destination);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(audioContext.destination);

    impactTone.start(when);
    impactTone.stop(when + 0.11);

    noiseSource.start(when);
    noiseSource.stop(when + 0.12);
};

const playCardClick = (audioContext, when, gainAmount) => {
    const clickOscillator = audioContext.createOscillator();
    const clickGain = audioContext.createGain();

    clickOscillator.type = "triangle";
    clickOscillator.frequency.setValueAtTime(1320, when);
    clickOscillator.frequency.exponentialRampToValueAtTime(880, when + 0.028);

    clickGain.gain.setValueAtTime(Math.max(0.006, gainAmount), when);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, when + 0.045);

    clickOscillator.connect(clickGain);
    clickGain.connect(audioContext.destination);

    clickOscillator.start(when);
    clickOscillator.stop(when + 0.05);
};

const playStanceSwish = (audioContext, noiseBuffer, when, gainAmount) => {
    // A short filtered sweep to mimic cloth/wind as stance shifts.
    playWhoosh(audioContext, noiseBuffer, when, gainAmount, 150, 720);
};

const playGravelHit = (audioContext, noiseBuffer, when, gainAmount) => {
    const source = audioContext.createBufferSource();
    source.buffer = noiseBuffer;

    const bandpass = audioContext.createBiquadFilter();
    bandpass.type = "bandpass";
    bandpass.frequency.setValueAtTime(520, when);
    bandpass.Q.setValueAtTime(1.3, when);

    const lowpass = audioContext.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.setValueAtTime(1300, when);

    const gainNode = audioContext.createGain();
    gainNode.gain.setValueAtTime(Math.max(0.01, gainAmount), when);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, when + 0.08);

    source.connect(bandpass);
    bandpass.connect(lowpass);
    lowpass.connect(gainNode);
    gainNode.connect(audioContext.destination);

    source.start(when);
    source.stop(when + 0.09);
};

const playShuffleGravel = (audioContext, noiseBuffer, when, gainAmount) => {
    // Two quick gritty hits for a stance-adjust style shuffle cue.
    playGravelHit(audioContext, noiseBuffer, when, gainAmount * 0.95);
    playGravelHit(audioContext, noiseBuffer, when + 0.1, gainAmount * 0.78);
};

const playBellStrike = (audioContext, noiseBuffer, when, gainAmount) => {
    const strikeBus = audioContext.createGain();
    const highpass = audioContext.createBiquadFilter();
    const delay = audioContext.createDelay(1.5);
    const feedback = audioContext.createGain();
    const damping = audioContext.createBiquadFilter();

    strikeBus.gain.setValueAtTime(1, when);
    highpass.type = "highpass";
    highpass.frequency.setValueAtTime(380, when);

    delay.delayTime.setValueAtTime(0.074, when);
    feedback.gain.setValueAtTime(0.43, when);
    damping.type = "lowpass";
    damping.frequency.setValueAtTime(3400, when);

    strikeBus.connect(highpass);
    highpass.connect(audioContext.destination);
    highpass.connect(delay);
    delay.connect(audioContext.destination);
    delay.connect(damping);
    damping.connect(feedback);
    feedback.connect(delay);

    const partials = [
        { freq: 980, amp: 1.0, decay: 1.9, type: "triangle" },
        { freq: 1410, amp: 0.68, decay: 1.55, type: "sine" },
        { freq: 1960, amp: 0.47, decay: 1.25, type: "triangle" },
        { freq: 2530, amp: 0.3, decay: 1.05, type: "sine" }
    ];

    partials.forEach(({ freq, amp, decay, type }) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = type;
        oscillator.frequency.setValueAtTime(freq, when);
        oscillator.frequency.exponentialRampToValueAtTime(freq * 0.992, when + 0.25);

        gainNode.gain.setValueAtTime(Math.max(0.0001, gainAmount * amp), when);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, when + decay);

        oscillator.connect(gainNode);
        gainNode.connect(strikeBus);

        oscillator.start(when);
        oscillator.stop(when + decay + 0.04);
    });

    const impactSource = audioContext.createBufferSource();
    impactSource.buffer = noiseBuffer;
    const impactHighpass = audioContext.createBiquadFilter();
    impactHighpass.type = "highpass";
    impactHighpass.frequency.setValueAtTime(2600, when);
    const impactGain = audioContext.createGain();
    impactGain.gain.setValueAtTime(Math.max(0.0001, gainAmount * 0.62), when);
    impactGain.gain.exponentialRampToValueAtTime(0.0001, when + 0.02);

    impactSource.connect(impactHighpass);
    impactHighpass.connect(impactGain);
    impactGain.connect(strikeBus);

    impactSource.start(when);
    impactSource.stop(when + 0.03);
};

const playBarrageBell = (audioContext, noiseBuffer, when, gainAmount) => {
    // Triple-ring barrage opener with sharp attack and a long metallic tail.
    playBellStrike(audioContext, noiseBuffer, when, gainAmount * 0.38);
    playBellStrike(audioContext, noiseBuffer, when + 0.24, gainAmount * 0.35);
    playBellStrike(audioContext, noiseBuffer, when + 0.48, gainAmount * 0.33);
};

const playSingleBell = (audioContext, noiseBuffer, when, gainAmount) => {
    playBellStrike(audioContext, noiseBuffer, when, gainAmount * 0.42);
};

const getInitialAudioPrefs = () => {
    try {
        const raw = localStorage.getItem(AUDIO_PREFS_KEY);
        if (!raw) {
            return {
                enabled: true,
                volume: 70
            };
        }

        const parsed = JSON.parse(raw);
        return {
            enabled: parsed?.enabled !== false,
            volume: clamp(Number(parsed?.volume) || 70, 0, 100)
        };
    } catch {
        return {
            enabled: true,
            volume: 70
        };
    }
};

function useSfx() {
    const initialPrefs = useMemo(getInitialAudioPrefs, []);
    const [isSfxEnabled, setIsSfxEnabled] = useState(initialPrefs.enabled);
    const [sfxVolume, setSfxVolume] = useState(initialPrefs.volume);

    const audioContextRef = useRef(null);
    const noiseBufferRef = useRef(null);

    const persistAudioPrefs = useCallback((nextEnabled, nextVolume) => {
        try {
            localStorage.setItem(
                AUDIO_PREFS_KEY,
                JSON.stringify({ enabled: nextEnabled, volume: clamp(nextVolume, 0, 100) })
            );
        } catch {
            // Ignore storage failures (private mode, quota exceeded).
        }
    }, []);

    const setSfxEnabled = useCallback(
        (nextValue) => {
            setIsSfxEnabled(Boolean(nextValue));
            persistAudioPrefs(Boolean(nextValue), sfxVolume);
        },
        [persistAudioPrefs, sfxVolume]
    );

    const setSfxVolumePercent = useCallback(
        (nextValue) => {
            const normalized = clamp(Number(nextValue) || 0, 0, 100);
            setSfxVolume(normalized);
            persistAudioPrefs(isSfxEnabled, normalized);
        },
        [isSfxEnabled, persistAudioPrefs]
    );

    const ensureAudioContext = useCallback(async () => {
        const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextCtor) {
            return null;
        }

        if (!audioContextRef.current) {
            audioContextRef.current = new AudioContextCtor();
        }

        const audioContext = audioContextRef.current;

        if (audioContext.state === "suspended") {
            try {
                await audioContext.resume();
            } catch {
                return null;
            }
        }

        if (!noiseBufferRef.current) {
            noiseBufferRef.current = createPinkNoiseBuffer(audioContext);
        }

        return audioContext;
    }, []);

    const unlockAudio = useCallback(() => {
        void ensureAudioContext();
    }, [ensureAudioContext]);

    const playSfx = useCallback(
        async (name, options = {}) => {
            if (!isSfxEnabled) {
                return;
            }

            const audioContext = await ensureAudioContext();
            if (!audioContext || !noiseBufferRef.current) {
                return;
            }

            const volumeMultiplier = clamp(Number(options?.volumeMultiplier) || 1, 0, 2);
            const baseGain = clamp((sfxVolume / 100) * volumeMultiplier, 0.01, 1.0);
            const now = audioContext.currentTime;

            switch (name) {
            case "jab":
                playWhoosh(audioContext, noiseBufferRef.current, now, baseGain * 0.45, 70, 900);
                break;
            case "cross":
                playWhoosh(audioContext, noiseBufferRef.current, now, baseGain * 0.55, 95, 700);
                break;
            case "hook":
                playWhoosh(audioContext, noiseBufferRef.current, now, baseGain * 0.58, 115, 560);
                break;
            case "uppercut":
                playWhoosh(audioContext, noiseBufferRef.current, now, baseGain * 0.62, 120, 480);
                break;
            case "impact_face":
                playImpact(audioContext, noiseBufferRef.current, now, baseGain * 0.6, false);
                break;
            case "impact_body":
                playImpact(audioContext, noiseBufferRef.current, now, baseGain * 0.56, true);
                break;
            case "click":
                playCardClick(audioContext, now, baseGain * 0.24);
                break;
            case "swish_stance":
                playStanceSwish(audioContext, noiseBufferRef.current, now, baseGain * 0.52);
                break;
            case "shuffle_gravel":
                playShuffleGravel(audioContext, noiseBufferRef.current, now, baseGain * 0.58);
                break;
            case "barrage_bell":
                playBarrageBell(audioContext, noiseBufferRef.current, now, baseGain);
                break;
            case "bell_strike":
                playSingleBell(audioContext, noiseBufferRef.current, now, baseGain);
                break;
            default:
                break;
            }
        },
        [ensureAudioContext, isSfxEnabled, sfxVolume]
    );

    return {
        isSfxEnabled,
        sfxVolume,
        setSfxEnabled,
        setSfxVolume: setSfxVolumePercent,
        unlockAudio,
        playSfx
    };
}

export default useSfx;
